// "API" page — only in the sidebar of customers the admin turned API access on for
// (Account.api_access_enabled). Create / revoke API keys, and the docs for the customer API
// (api.fbzipper.com routes/customer-api.js, /api/v1/me/…): campaigns and posts.
import {FC, useEffect, useState} from 'react'
import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL
const BASE = 'https://api.fbzipper.com/api/v1/me'

type Key = {_id: string; name: string; key_prefix: string; createdAt: string; last_used_at?: string; requests?: number}

const when = (iso?: string) =>
  iso ? new Date(iso).toLocaleString('he-IL', {dateStyle: 'short', timeStyle: 'short'}) : '—'

const Code: FC<{children: string}> = ({children}) => (
  <pre
    dir='ltr'
    style={{background: '#0f1629', color: '#e6edf3', borderRadius: 10, padding: '12px 14px', fontSize: 12.5, overflowX: 'auto', textAlign: 'left', margin: 0}}
  >
    {children}
  </pre>
)

type Endpoint = {method: string; path: string; desc: string; body?: string; response?: string}

const ENDPOINTS: {title: string; items: Endpoint[]}[] = [
  {
    title: 'חשבון',
    items: [{method: 'GET', path: '/', desc: 'בדיקת המפתח: פרטי החשבון', response: `{ "success": true, "account": { "id": "…", "name": "…", "email": "…" } }`}],
  },
  {
    title: 'קמפיינים',
    items: [
      {method: 'GET', path: '/campaigns', desc: 'כל הקמפיינים של החשבון', response: `{ "success": true, "campaigns": [ { "id": "…", "name": "דירות בתל אביב", "posts": 4, "created_at": "…" } ] }`},
      {method: 'POST', path: '/campaigns', desc: 'יצירת קמפיין', body: `{ "name": "דירות בתל אביב" }`, response: `{ "success": true, "campaign": { "id": "…", "name": "דירות בתל אביב", "posts": 0 } }`},
      {method: 'GET', path: '/campaigns/{id}', desc: 'קמפיין אחד'},
      {method: 'PATCH', path: '/campaigns/{id}', desc: 'שינוי שם', body: `{ "name": "שם חדש" }`},
      {method: 'DELETE', path: '/campaigns/{id}', desc: 'מחיקת קמפיין, כולל הפוסטים שלו (ניתן לשחזור דרך התמיכה)'},
    ],
  },
  {
    title: 'פוסטים',
    items: [
      {method: 'GET', path: '/campaigns/{id}/posts', desc: 'כל הפוסטים בקמפיין'},
      {
        method: 'POST',
        path: '/campaigns/{id}/posts',
        desc: 'יצירת פוסט בקמפיין',
        body: `{
  "name": "דירת 4 חדרים בפלורנטין",
  "content": "טקסט הפוסט כפי שיפורסם בקבוצות",
  "media": ["https://example.com/photo1.jpg", "https://example.com/photo2.jpg"],
  "reply_text": "תשובה אוטומטית למי שמגיב (לא חובה)",
  "active": true
}`,
        response: `{ "success": true, "post": { "id": "…", "campaign_id": "…", "name": "…", "content": "…", "media": [ … ], "type": "album", "active": true } }`,
      },
      {method: 'GET', path: '/posts/{id}', desc: 'פוסט אחד'},
      {method: 'PATCH', path: '/posts/{id}', desc: 'עדכון פוסט: שולחים רק את השדות שמשתנים', body: `{ "content": "טקסט מעודכן", "active": false }`},
      {method: 'DELETE', path: '/posts/{id}', desc: 'מחיקת פוסט'},
    ],
  },
]

const METHOD_COLOR: Record<string, string> = {GET: '#16a34a', POST: '#2563eb', PATCH: '#d97706', DELETE: '#dc2626'}

const ApiAccessPage: FC = () => {
  const [keys, setKeys] = useState<Key[]>([])
  const [name, setName] = useState('')
  const [secret, setSecret] = useState('')
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const load = async () => {
    try {
      const {data} = await axios.get(`${API_URL}/user/api-keys`)
      setKeys(data.keys || [])
    } catch (e: any) {
      setError(e?.response?.data?.error || 'שגיאה בטעינת המפתחות')
    }
  }
  useEffect(() => {
    load()
  }, [])

  const create = async () => {
    setBusy(true)
    setError('')
    try {
      const {data} = await axios.post(`${API_URL}/user/api-keys`, {name: name.trim()})
      setSecret(data.secret)
      setCopied(false)
      setName('')
      load()
    } catch (e: any) {
      setError(e?.response?.data?.error || 'שגיאה ביצירת מפתח')
    } finally {
      setBusy(false)
    }
  }

  const revoke = async (k: Key) => {
    if (!window.confirm(`לבטל את המפתח ${k.key_prefix}…? מערכות שמשתמשות בו יפסיקו לעבוד.`)) return
    await axios.delete(`${API_URL}/user/api-keys/${k._id}`).catch(() => {})
    load()
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret)
      setCopied(true)
    } catch {}
  }

  return (
    <div dir='rtl' className='container-fluid' style={{maxWidth: 980}}>
      <h1 className='fw-bolder mb-1'>API</h1>
      <div className='text-gray-600 fs-6 mb-6'>
        ניהול הקמפיינים והפוסטים שלך מתוך המערכות שלך: יצירה, עדכון ומחיקה, עם מפתח אישי.
      </div>

      {/* Keys */}
      <div className='card mb-6'>
        <div className='card-body'>
          <h3 className='fw-bold mb-4'>🔑 מפתחות API</h3>
          {error && <div className='alert alert-danger py-2'>{error}</div>}

          {secret && (
            <div className='alert alert-warning'>
              <div className='fw-bold mb-2'>המפתח החדש שלך. העתיקו אותו עכשיו: הוא לא יוצג שוב.</div>
              <div className='d-flex gap-2 align-items-center flex-wrap'>
                <code dir='ltr' style={{fontSize: 14, padding: '6px 10px', background: '#fff', borderRadius: 6, wordBreak: 'break-all'}}>
                  {secret}
                </code>
                <button className='btn btn-sm btn-primary' onClick={copy}>
                  {copied ? '✓ הועתק' : 'העתקה'}
                </button>
              </div>
            </div>
          )}

          <div className='d-flex gap-2 mb-5 flex-wrap'>
            <input
              className='form-control'
              style={{maxWidth: 280}}
              placeholder='שם למפתח (למשל: האתר שלי)'
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
            />
            <button className='btn btn-primary' onClick={create} disabled={busy}>
              + מפתח חדש
            </button>
          </div>

          {keys.length ? (
            <div className='table-responsive'>
              <table className='table align-middle fs-7'>
                <thead>
                  <tr className='text-gray-500 fw-bold'>
                    <th>שם</th>
                    <th>מפתח</th>
                    <th>נוצר</th>
                    <th>שימוש אחרון</th>
                    <th>בקשות</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => (
                    <tr key={k._id}>
                      <td>{k.name || '—'}</td>
                      <td dir='ltr' style={{textAlign: 'right'}}>
                        <code>{k.key_prefix}…</code>
                      </td>
                      <td>{when(k.createdAt)}</td>
                      <td>{when(k.last_used_at)}</td>
                      <td>{k.requests || 0}</td>
                      <td>
                        <button className='btn btn-sm btn-light-danger' onClick={() => revoke(k)}>
                          ביטול
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='text-gray-500'>עדיין אין מפתחות. צרו מפתח כדי להתחיל.</div>
          )}
        </div>
      </div>

      {/* Docs */}
      <div className='card mb-6'>
        <div className='card-body'>
          <h3 className='fw-bold mb-4'>📖 תיעוד</h3>
          <div className='mb-2 fw-bold'>כתובת בסיס</div>
          <Code>{BASE}</Code>

          <div className='mt-5 mb-2 fw-bold'>הזדהות</div>
          <div className='text-gray-700 mb-2'>כל בקשה שולחת את המפתח בכותרת Authorization:</div>
          <Code>{`Authorization: Bearer fbz_cust_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx`}</Code>
          <ul className='text-gray-700 mt-3 mb-0'>
            <li>גוף הבקשה והתשובה ב-JSON. כל תשובה כוללת "success": true או false.</li>
            <li>עד 120 בקשות בדקה לכל מפתח.</li>
            <li>תמונות וסרטונים: כתובות https ציבוריות, עד 20 לפוסט.</li>
            <li>מחיקה היא רכה: אפשר לשחזר דרך התמיכה.</li>
          </ul>

          <div className='mt-5 mb-2 fw-bold'>דוגמה: פוסט חדש בקמפיין</div>
          <Code>{`# 1. יצירת קמפיין
curl -X POST ${BASE}/campaigns \\
  -H "Authorization: Bearer $FBZ_KEY" -H "Content-Type: application/json" \\
  -d '{"name":"דירות בתל אביב"}'

# 2. פוסט בקמפיין (id מהתשובה הקודמת)
curl -X POST ${BASE}/campaigns/CAMPAIGN_ID/posts \\
  -H "Authorization: Bearer $FBZ_KEY" -H "Content-Type: application/json" \\
  -d '{"name":"4 חדרים בפלורנטין","content":"דירה מהממת…","media":["https://example.com/1.jpg"]}'`}</Code>

          {ENDPOINTS.map((g) => (
            <div key={g.title} className='mt-7'>
              <h4 className='fw-bold mb-3'>{g.title}</h4>
              {g.items.map((ep) => (
                <div key={ep.method + ep.path} className='border rounded p-3 mb-3'>
                  <div className='d-flex align-items-center gap-2 flex-wrap mb-1' dir='ltr' style={{justifyContent: 'flex-end'}}>
                    <code style={{fontSize: 13}}>{ep.path}</code>
                    <span
                      className='badge'
                      style={{background: METHOD_COLOR[ep.method], color: '#fff', minWidth: 58, justifyContent: 'center'}}
                    >
                      {ep.method}
                    </span>
                  </div>
                  <div className='text-gray-700 mb-2'>{ep.desc}</div>
                  {ep.body && (
                    <>
                      <div className='fs-8 text-gray-500 mb-1'>גוף הבקשה</div>
                      <Code>{ep.body}</Code>
                    </>
                  )}
                  {ep.response && (
                    <>
                      <div className='fs-8 text-gray-500 mb-1 mt-2'>תשובה</div>
                      <Code>{ep.response}</Code>
                    </>
                  )}
                </div>
              ))}
            </div>
          ))}

          <h4 className='fw-bold mt-7 mb-3'>שגיאות</h4>
          <Code>{`{ "success": false, "error": "not_found", "message": "Campaign not found." }

401 missing_api_key / invalid_api_key   מפתח חסר או לא תקין
403 api_disabled                        גישת ה-API כבויה לחשבון
404 not_found                           קמפיין / פוסט לא נמצא (או לא שייך לחשבון)
422 name_required / content_required / invalid_media
429 rate_limited                        יותר מ-120 בקשות בדקה`}</Code>
        </div>
      </div>
    </div>
  )
}

export default ApiAccessPage
